import {
	CircleHelp,
	Eye,
	FolderGit2,
	GitPullRequest,
	Gitlab,
	Maximize2,
	Minimize2,
	RefreshCcw,
} from "lucide-react";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useAuth } from "../AuthContext";
import {
	DIFF_OPEN_IN_NEW_WINDOW_STORAGE_KEY,
	GITLAB_BASE_URL,
	VIEW_MODE,
} from "../constants";
import { isMRExcludedByRules } from "../exclusionRules";
import { fuzzyMatchFields, normalizeSearchQuery } from "../fuzzySearch";
import { useIgnoredMRs } from "../hooks/useIgnoredMRs";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { useMRTags } from "../hooks/useMRTags";
import { useMergeRequests } from "../hooks/useMergeRequests";
import { useRecentPushes } from "../hooks/useRecentPushes";
import { getWatchKey, useWatchedMRs } from "../hooks/useWatchedMRs";
import { applyQuickFilters } from "../quickFilters";
import { AvatarPreloader } from "./AvatarPreloader";
import { FilterButton } from "./FilterButton";
import { FreshnessIndicator } from "./FreshnessIndicator";
import { IconLegendModal } from "./IconLegendModal";
import { MRDiffViewer } from "./MRDiffViewer";
import { TagQuickFilter, normalizeTagFilterValue } from "./MRTags";
import { NotificationBell } from "./NotificationBell";
import { QuickFilters } from "./QuickFilters";
import { ReposPage } from "./ReposPage";
import { ReviewBoard } from "./ReviewBoard";
import { SearchBar } from "./SearchBar";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { RepoView } from "./dashboardViews/repo";
import { StandardView } from "./dashboardViews/standard";
import { SwimlaneView } from "./dashboardViews/swimlane";

const getMRKey = getWatchKey;

const DASHBOARD_TAB = Object.freeze({
	MAIN: "main",
	WATCHING: "watching",
	REPOS: "repos",
});

const MAX_HIDDEN_MR_SNAPSHOTS = 500;
const MAX_DIFF_MR_SNAPSHOTS = 50;
const DIFF_QUERY_PARAM = "diff";
const DIFF_FILE_QUERY_PARAM = "file";
const DIFF_WINDOW_QUERY_PARAM = "diffWindow";
const DIFF_MR_SNAPSHOTS_STORAGE_KEY = "dashboard-diff-mr-snapshots";
const DASHBOARD_FULL_WIDTH_STORAGE_KEY = "dashboard-full-width";
const DIFF_WINDOW_WIDTH = 1440;
const DIFF_WINDOW_HEIGHT = 960;

const getProjectTagPath = (tag) =>
	typeof tag === "string" ? tag : tag?.id || tag?.text || "";

const getMRId = (mrOrId) =>
	typeof mrOrId === "string" ? mrOrId : mrOrId?.id || "";

const getMRProjectPath = (mr) =>
	mr?.project?.fullPath ||
	mr?.projectPath ||
	mr?.reference?.split("!")[0] ||
	"";

const getMRIid = (mr) =>
	mr?.iid ||
	(typeof mr?.reference === "string" ? mr.reference.split("!")[1] : "");

const getDiffMRKey = (mr) => {
	const projectPath = getMRProjectPath(mr);
	const iid = getMRIid(mr);
	return projectPath && iid ? `${projectPath}!${iid}` : getMRKey(mr);
};

const parseDiffMRKey = (key) => {
	const value = String(key || "");
	const delimiterIndex = value.lastIndexOf("!");
	if (delimiterIndex <= 0 || delimiterIndex === value.length - 1) return null;

	return {
		projectPath: value.slice(0, delimiterIndex),
		iid: value.slice(delimiterIndex + 1),
	};
};

const encodePathSegments = (path) =>
	String(path || "")
		.split("/")
		.map((segment) => encodeURIComponent(segment))
		.join("/");

const buildMergeRequestUrl = ({ projectPath, iid }) =>
	projectPath && iid
		? `${GITLAB_BASE_URL}/${encodePathSegments(projectPath)}/-/merge_requests/${encodeURIComponent(iid)}`
		: "";

const createDiffMRFromKey = (key) => {
	const parsed = parseDiffMRKey(key);
	if (!parsed) return null;

	return {
		id: key,
		iid: parsed.iid,
		title: `Diff for !${parsed.iid}`,
		projectPath: parsed.projectPath,
		project: { fullPath: parsed.projectPath },
		reference: key,
		webUrl: buildMergeRequestUrl(parsed),
	};
};

const readSnapshotMR = (snapshot) => snapshot?.mr || snapshot;

const readDiffKeyFromLocation = () => {
	if (typeof window === "undefined") return "";
	return (
		new URLSearchParams(window.location.search).get(DIFF_QUERY_PARAM) || ""
	);
};

const readDiffFileFromLocation = () => {
	if (typeof window === "undefined") return "";
	return (
		new URLSearchParams(window.location.search).get(DIFF_FILE_QUERY_PARAM) || ""
	);
};

const readDiffWindowFromLocation = () => {
	if (typeof window === "undefined") return false;
	return (
		new URLSearchParams(window.location.search).get(DIFF_WINDOW_QUERY_PARAM) ===
		"1"
	);
};

const getDiffWindowFeatures = () => {
	if (typeof window === "undefined") return "";

	const availableWidth = window.screen?.availWidth || DIFF_WINDOW_WIDTH;
	const availableHeight = window.screen?.availHeight || DIFF_WINDOW_HEIGHT;
	const width = Math.min(DIFF_WINDOW_WIDTH, availableWidth);
	const height = Math.min(DIFF_WINDOW_HEIGHT, availableHeight);
	const left = Math.max(
		0,
		Math.round((availableWidth - width) / 2 + (window.screen?.availLeft || 0)),
	);
	const top = Math.max(
		0,
		Math.round((availableHeight - height) / 2 + (window.screen?.availTop || 0)),
	);

	return [
		"popup=yes",
		"resizable=yes",
		"scrollbars=yes",
		`width=${width}`,
		`height=${height}`,
		`left=${left}`,
		`top=${top}`,
	].join(",");
};

const buildDiffWindowUrl = (diffKey) => {
	if (typeof window === "undefined") return "";

	const url = new URL(window.location.href);
	url.searchParams.set(DIFF_QUERY_PARAM, diffKey);
	url.searchParams.delete(DIFF_FILE_QUERY_PARAM);
	url.searchParams.set(DIFF_WINDOW_QUERY_PARAM, "1");

	return url.href;
};

const openDiffWindow = (diffKey) => {
	if (typeof window === "undefined") return false;

	const diffWindow = window.open(
		buildDiffWindowUrl(diffKey),
		"_blank",
		getDiffWindowFeatures(),
	);
	diffWindow?.focus?.();

	return Boolean(diffWindow);
};

const writeDiffKeyToHistory = (
	diffKey,
	{ replace = false, clearFile = false } = {},
) => {
	if (typeof window === "undefined") return;

	const url = new URL(window.location.href);
	if (diffKey) {
		url.searchParams.set(DIFF_QUERY_PARAM, diffKey);
	} else {
		url.searchParams.delete(DIFF_QUERY_PARAM);
	}
	if (!diffKey || clearFile) {
		url.searchParams.delete(DIFF_FILE_QUERY_PARAM);
	}
	if (!diffKey) {
		url.searchParams.delete(DIFF_WINDOW_QUERY_PARAM);
	}

	const historyState = {
		...(window.history.state || {}),
		dashboardDiffKey: diffKey || null,
		...(!diffKey || clearFile ? { dashboardDiffFile: null } : {}),
	};
	const nextUrl = `${url.pathname}${url.search}${url.hash}`;
	const method = replace ? "replaceState" : "pushState";
	window.history[method](historyState, "", nextUrl);
};

const writeJsonToLocalStorage = (key, value) => {
	try {
		localStorage.setItem(key, JSON.stringify(value));
	} catch (error) {
		console.error(`Error writing localStorage key "${key}":`, error);
	}
};

const trimDiffMRSnapshots = (snapshots) =>
	Object.fromEntries(
		Object.entries(snapshots)
			.sort(([, a], [, b]) => {
				const left = new Date(a?.savedAt || a?.mr?.updatedAt || 0);
				const right = new Date(b?.savedAt || b?.mr?.updatedAt || 0);
				return right - left;
			})
			.slice(0, MAX_DIFF_MR_SNAPSHOTS),
	);

const getMRSearchFields = (mr) => [
	mr.title,
	mr.author?.name,
	mr.author?.username,
	mr.projectPath,
	mr.project?.fullPath,
	mr.iid && `!${mr.iid}`,
	mr.iid && `#${mr.iid}`,
	mr.sourceBranch,
];

const toTitleCase = (value) =>
	typeof value === "string"
		? value
				.trim()
				.replace(
					/\S+/g,
					(word) =>
						`${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`,
				)
		: "";

const resolveWatchedMRs = (watchedIds, snapshots, knownMRs) => {
	const knownMRByKey = new Map();
	for (const mr of knownMRs) {
		knownMRByKey.set(getMRKey(mr), mr);
	}

	return watchedIds
		.map((key) => knownMRByKey.get(key) || snapshots[key]?.mr)
		.filter(Boolean);
};

export const Dashboard = () => {
	const { user, logout } = useAuth();
	const [showDrafts, setShowDrafts] = useLocalStorage("showDrafts", true);
	const [showBotPRs, setShowBotPRs] = useLocalStorage("showBotPRs", true);
	const [sortOption, setSortOption] = useLocalStorage(
		"sortOption",
		"updatedAt",
	);
	const [showPendingWithProjectRules, setShowPendingWithProjectRules] =
		useLocalStorage("showPendingWithProjectRules", true);
	const [showPendingWithAllApprovals, setShowPendingWithAllApprovals] =
		useLocalStorage("showPendingWithAllApprovals", true);
	const [watchProjects, setWatchProjects] = useLocalStorage(
		"watchProjects",
		[],
	);
	const [excludeProjects, setExcludeProjects] = useLocalStorage(
		"excludeProjects",
		[],
	);
	const [greyOutBlockingComments, setGreyOutBlockingComments] = useLocalStorage(
		"greyOutBlockingComments",
		true,
	);
	const [enableCommentsExpansion, setEnableCommentsExpansion] = useLocalStorage(
		"enableCommentsExpansion",
		false,
	);
	// View mode: standard, swimlane, or reviewboard
	const [viewMode, setViewMode] = useLocalStorage(
		"viewMode",
		VIEW_MODE.STANDARD,
	);
	const [activeTab, setActiveTab] = useLocalStorage(
		"dashboard-active-tab",
		DASHBOARD_TAB.MAIN,
	);
	const [isFullWidth, setIsFullWidth] = useLocalStorage(
		DASHBOARD_FULL_WIDTH_STORAGE_KEY,
		false,
	);
	const [openDiffInNewWindow] = useLocalStorage(
		DIFF_OPEN_IN_NEW_WINDOW_STORAGE_KEY,
		false,
	);

	// Migration: convert old useSwimlaneView boolean to new viewMode string
	useEffect(() => {
		const oldValue = localStorage.getItem("useSwimlaneView");
		if (oldValue !== null) {
			const wasEnabled = JSON.parse(oldValue);
			if (wasEnabled && viewMode === VIEW_MODE.STANDARD) {
				setViewMode(VIEW_MODE.SWIMLANE);
			}
			localStorage.removeItem("useSwimlaneView");
		}
	}, [setViewMode, viewMode]);

	const [showSwimlaneMergedColumn, setShowSwimlaneMergedColumn] =
		useLocalStorage("showSwimlaneMergedColumn", true);
	const [showSwimlaneDeletedColumn, setShowSwimlaneDeletedColumn] =
		useLocalStorage("showSwimlaneDeletedColumn", false);
	const [useLocalTime, setUseLocalTime] = useLocalStorage(
		"useLocalTime",
		false,
	);
	const [
		swimlaneHideCreatedDaysThreshold,
		setSwimlaneHideCreatedDaysThreshold,
	] = useLocalStorage("swimlaneHideCreatedDaysThreshold", 30);
	const [
		swimlaneHideUpdatedDaysThreshold,
		setSwimlaneHideUpdatedDaysThreshold,
	] = useLocalStorage("swimlaneHideUpdatedDaysThreshold", 14);
	const [searchQuery, setSearchQuery] = useState("");
	const [showSearch, setShowSearch] = useState(false);
	const [showIconLegend, setShowIconLegend] = useState(false);
	const [diffKey, setDiffKey] = useState(readDiffKeyFromLocation);
	const [quickFilters, setQuickFilters] = useLocalStorage("quickFilters", {});
	const [activeTagFilter, setActiveTagFilter] = useLocalStorage(
		"dashboard-active-tag-filter",
		[],
	);
	const activeTagFilters = useMemo(
		() => normalizeTagFilterValue(activeTagFilter),
		[activeTagFilter],
	);
	const [excludeLikeRules, setExcludeLikeRules] = useLocalStorage(
		"dashboard-exclude-like-rules",
		[],
	);
	const tabsRef = useRef(null);
	const [tagFilterWidth, setTagFilterWidth] = useState(null);
	const [diffMRSnapshots, setDiffMRSnapshots] = useLocalStorage(
		DIFF_MR_SNAPSHOTS_STORAGE_KEY,
		{},
	);
	const [hiddenMRSnapshots, setHiddenMRSnapshots] = useLocalStorage(
		"dashboard-hidden-mr-snapshots",
		{},
	);
	const {
		ignoredIds,
		ignoreMR: persistIgnoredMR,
		unignoreMR: persistUnignoredMR,
	} = useIgnoredMRs();
	const watchedMRState = useWatchedMRs();
	const { watchedIds, snapshots, getActivity, markSeen, markAllSeen } =
		watchedMRState;
	const {
		allTags: allMRTags,
		getTags: getMRTags,
		addTag: addMRTag,
		removeTag: removeMRTag,
		hasTag: mrHasTag,
	} = useMRTags();

	useEffect(() => {
		if (JSON.stringify(activeTagFilter) === JSON.stringify(activeTagFilters)) {
			return;
		}

		setActiveTagFilter(activeTagFilters);
	}, [activeTagFilter, activeTagFilters, setActiveTagFilter]);

	useEffect(() => {
		const tabList = tabsRef.current;
		if (!tabList) return undefined;

		const updateTagFilterWidth = () => {
			setTagFilterWidth(tabList.getBoundingClientRect().width);
		};

		updateTagFilterWidth();
		if (typeof ResizeObserver === "undefined") {
			window.addEventListener("resize", updateTagFilterWidth);
			return () => {
				window.removeEventListener("resize", updateTagFilterWidth);
			};
		}

		const resizeObserver = new ResizeObserver(updateTagFilterWidth);
		resizeObserver.observe(tabList);
		window.addEventListener("resize", updateTagFilterWidth);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener("resize", updateTagFilterWidth);
		};
	}, []);

	const toggleQuickFilter = (id) =>
		setQuickFilters((prev) => ({ ...prev, [id]: !prev[id] }));
	const clearQuickFilters = () => setQuickFilters({});

	const {
		pendingReviewMRs,
		myOpenMRs,
		myMergedMRs,
		myClosedMRs,
		recentlyClosedMRs,
		loading,
		loadingClosed,
		error,
		refetch,
		watchedMRs,
		username: gitlabUsername,
		isRefreshing,
		hasIncompleteResults,
		lastUpdated,
		closeMergeRequest,
	} = useMergeRequests(watchProjects, watchedIds);

	const { pushesWithoutMR } = useRecentPushes();
	const normalizedSearchQuery = normalizeSearchQuery(searchQuery);

	useEffect(() => {
		const handlePopState = () => setDiffKey(readDiffKeyFromLocation());
		window.addEventListener("popstate", handlePopState);
		return () => window.removeEventListener("popstate", handlePopState);
	}, []);

	const refreshData = async () => {
		await refetch();
	};

	const filterMRs = (mrs, { includeDrafts = false } = {}) => {
		const excludeProjectPaths = excludeProjects.map(getProjectTagPath);

		const base = mrs.filter((mr) => {
			if (!includeDrafts && !showDrafts && mr.draft) return false;
			if (!showBotPRs && (mr.author?.bot ?? false)) return false;
			if (
				activeTagFilters.length > 0 &&
				!activeTagFilters.some((tag) => mrHasTag(mr, tag))
			) {
				return false;
			}
			if (
				normalizedSearchQuery &&
				!fuzzyMatchFields(getMRSearchFields(mr), normalizedSearchQuery)
			) {
				return false;
			}
			// Check both project.fullPath and projectPath for exclusion
			const projectPath = mr.project?.fullPath || mr.projectPath;
			if (excludeProjectPaths.includes(projectPath)) return false;
			return true;
		});

		return applyQuickFilters(base, quickFilters);
	};

	const isRuleExcluded = (mr) => isMRExcludedByRules(mr, excludeLikeRules);
	const isIgnoredMR = (mr) => ignoredIds.includes(mr.id);

	const splitHiddenMRs = (mrs) => {
		const active = [];
		const hidden = [];

		for (const mr of mrs) {
			if (isRuleExcluded(mr) || isIgnoredMR(mr)) {
				hidden.push(mr);
			} else {
				active.push(mr);
			}
		}

		return { active, hidden };
	};

	const dedupeMRs = (mrs) => {
		const seen = new Set();
		return mrs.filter((mr) => {
			const key = getMRKey(mr);
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	};

	const hiddenSnapshotMRs = Object.values(hiddenMRSnapshots || {})
		.map((snapshot) => snapshot?.mr || snapshot)
		.filter(Boolean);

	const resolvedWatchedMRs = resolveWatchedMRs(watchedIds, snapshots, [
		...pendingReviewMRs,
		...myOpenMRs,
		...recentlyClosedMRs,
		...(watchedMRs || []),
	]);
	const notificationItems = resolvedWatchedMRs
		.map((mr) => ({
			key: getMRKey(mr),
			mr,
			activity: getActivity(mr),
		}))
		.filter((item) => item.activity?.length);

	const rememberHiddenMRs = (mrs) => {
		const items = (Array.isArray(mrs) ? mrs : [mrs]).filter(
			(mr) => mr && typeof mr === "object" && getMRKey(mr),
		);
		if (items.length === 0) return;

		const savedAt = new Date().toISOString();
		setHiddenMRSnapshots((prev) => {
			const next = { ...(prev || {}) };

			for (const mr of items) {
				next[getMRKey(mr)] = { savedAt, mr };
			}

			return Object.fromEntries(
				Object.entries(next)
					.sort(([, a], [, b]) => {
						const left = new Date(a?.savedAt || a?.mr?.updatedAt || 0);
						const right = new Date(b?.savedAt || b?.mr?.updatedAt || 0);
						return right - left;
					})
					.slice(0, MAX_HIDDEN_MR_SNAPSHOTS),
			);
		});
	};

	const rememberDiffMR = useCallback(
		(mr) => {
			if (!mr || typeof mr !== "object") return;

			const key = getDiffMRKey(mr);
			if (!key) return;

			const savedAt = new Date().toISOString();
			const legacyKey = getMRKey(mr);
			const next = trimDiffMRSnapshots({
				...(diffMRSnapshots || {}),
				[key]: { savedAt, mr },
				...(legacyKey && legacyKey !== key
					? { [legacyKey]: { savedAt, mr } }
					: {}),
			});

			setDiffMRSnapshots(next);
			writeJsonToLocalStorage(DIFF_MR_SNAPSHOTS_STORAGE_KEY, next);
		},
		[diffMRSnapshots, setDiffMRSnapshots],
	);

	const openDiffMR = (mr) => {
		if (!mr) return;

		const key = getDiffMRKey(mr);
		if (!key) return;

		rememberDiffMR(mr);
		if (openDiffInNewWindow && openDiffWindow(key)) return;

		setDiffKey(key);
		if (readDiffKeyFromLocation() !== key || readDiffFileFromLocation()) {
			writeDiffKeyToHistory(key, { clearFile: true });
		}
	};

	const closeDiffMR = () => {
		if (readDiffWindowFromLocation()) {
			window.close();
			window.setTimeout(() => {
				setDiffKey("");
				writeDiffKeyToHistory("", { replace: true });
			}, 100);
			return;
		}

		const currentDiffKey = readDiffKeyFromLocation();

		if (
			currentDiffKey &&
			window.history.state?.dashboardDiffKey === currentDiffKey
		) {
			window.history.back();
			return;
		}

		setDiffKey("");
		if (currentDiffKey) {
			writeDiffKeyToHistory("", { replace: true });
		}
	};

	const ignoreMR = (mrOrId) => {
		const mrId = getMRId(mrOrId);
		if (!mrId) return;

		rememberHiddenMRs(mrOrId);
		persistIgnoredMR(mrId);
	};

	const unignoreMR = (mrOrId) => {
		const mrId = getMRId(mrOrId);
		if (!mrId) return;

		persistUnignoredMR(mrId);
	};

	const addExcludeLikeRule = (rule, sourceMRs = []) => {
		rememberHiddenMRs(sourceMRs);
		setExcludeLikeRules((prev) => {
			const nextRule = {
				...rule,
				id:
					rule.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
			};
			const ruleKey = (item) =>
				[
					item.titleRegex || "",
					item.projectPath || "",
					item.authorName || "",
					item.authorUsername || "",
				].join("\n");

			if (prev.some((item) => ruleKey(item) === ruleKey(nextRule))) {
				return prev;
			}

			return [...prev, nextRule];
		});
	};

	const removeExcludeLikeRule = (ruleId) => {
		setExcludeLikeRules((prev) => prev.filter((rule) => rule.id !== ruleId));
	};

	const clearExcludeLikeRules = () => setExcludeLikeRules([]);

	const basePendingReviewMRs = filterMRs(pendingReviewMRs);
	const standardBasePendingReviewMRs = filterMRs(pendingReviewMRs, {
		includeDrafts: true,
	});
	const baseMyOpenMRs = filterMRs(myOpenMRs);
	const baseMyMergedMRs = filterMRs(myMergedMRs || []);
	const baseMyClosedMRs = filterMRs(myClosedMRs || []);
	const baseRecentlyClosedMRs = filterMRs(recentlyClosedMRs);

	const pendingReviewSplit = splitHiddenMRs(basePendingReviewMRs);
	const standardPendingReviewSplit = splitHiddenMRs(
		standardBasePendingReviewMRs,
	);
	const myOpenSplit = splitHiddenMRs(baseMyOpenMRs);
	const myMergedSplit = splitHiddenMRs(baseMyMergedMRs);
	const myClosedSplit = splitHiddenMRs(baseMyClosedMRs);
	const recentlyClosedSplit = splitHiddenMRs(baseRecentlyClosedMRs);

	const filteredPendingReviewMRs = pendingReviewSplit.active;
	const standardFilteredPendingReviewMRs = standardPendingReviewSplit.active;
	const filteredMyOpenMRs = myOpenSplit.active;
	const filteredMyMergedMRs = myMergedSplit.active;
	const filteredMyClosedMRs = myClosedSplit.active;
	const filteredRecentlyClosedMRs = recentlyClosedSplit.active;
	const reviewBoardPendingReviewMRs = basePendingReviewMRs.filter(
		(mr) => !isRuleExcluded(mr),
	);
	const reviewBoardMyOpenMRs = baseMyOpenMRs.filter(
		(mr) => !isRuleExcluded(mr),
	);
	const reviewBoardRecentlyClosedMRs = baseRecentlyClosedMRs.filter(
		(mr) => !isRuleExcluded(mr),
	);
	const standardExcludedMRs = dedupeMRs([
		...standardPendingReviewSplit.hidden,
		...myOpenSplit.hidden,
		...recentlyClosedSplit.hidden,
		...hiddenSnapshotMRs,
		...resolvedWatchedMRs,
	]).filter((mr) => isRuleExcluded(mr) || isIgnoredMR(mr));
	const knownDiffMRs = dedupeMRs([
		...pendingReviewMRs,
		...myOpenMRs,
		...recentlyClosedMRs,
		...(watchedMRs || []),
		...resolvedWatchedMRs,
		...hiddenSnapshotMRs,
		...Object.values(diffMRSnapshots || {})
			.map(readSnapshotMR)
			.filter(Boolean),
	]);
	const diffMR = diffKey
		? knownDiffMRs.find(
				(mr) => getDiffMRKey(mr) === diffKey || getMRKey(mr) === diffKey,
			) || createDiffMRFromKey(diffKey)
		: null;

	useEffect(() => {
		if (!diffKey || !diffMR) return;

		const canonicalKey = getDiffMRKey(diffMR);
		if (
			!canonicalKey ||
			canonicalKey === diffKey ||
			readDiffKeyFromLocation() !== diffKey
		) {
			return;
		}

		rememberDiffMR(diffMR);
		setDiffKey(canonicalKey);
		writeDiffKeyToHistory(canonicalKey, { replace: true });
	}, [diffKey, diffMR, rememberDiffMR]);

	const profileUsername =
		gitlabUsername ||
		user?.profile?.username ||
		user?.profile?.preferred_username ||
		user?.profile?.nickname ||
		"";
	const profileName = toTitleCase(user?.profile?.name);
	const profileLabel = profileName || profileUsername || "GitLab";
	const profileUrl = profileUsername
		? `${GITLAB_BASE_URL}/${encodeURIComponent(profileUsername)}`
		: "";
	const dashboardWidthClassName = isFullWidth
		? "mx-auto max-w-none px-2"
		: "mx-auto max-w-[980px] px-2";

	return (
		<div className="min-h-screen bg-[var(--color-bg-app)]">
			{diffMR && <MRDiffViewer mr={diffMR} onClose={closeDiffMR} />}
			<IconLegendModal
				open={showIconLegend}
				onClose={() => setShowIconLegend(false)}
			/>
			<AvatarPreloader
				pendingReviewMRs={pendingReviewMRs}
				myOpenMRs={myOpenMRs}
				recentlyClosedMRs={recentlyClosedMRs}
			/>
			<div className={`${dashboardWidthClassName} pt-2 pb-2`}>
				<div className="bg-[var(--color-bg-primary)] rounded shadow px-3 py-2">
					<div className="flex justify-between items-center gap-3 min-h-8">
						{profileUrl ? (
							<a
								href={profileUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="group relative inline-flex shrink-0 items-center gap-1.5 text-[var(--color-text-primary)] hover:text-[var(--color-link)]"
								aria-label={`Open ${profileLabel} GitLab profile`}
							>
								<Gitlab className="h-4 w-4 shrink-0 self-center" />
								<span className="text-sm font-semibold leading-none underline underline-offset-2">
									{profileLabel}
								</span>
								<span className="pointer-events-none absolute top-1/2 left-full z-20 ml-2 -translate-y-1/2 whitespace-nowrap rounded border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-2 py-1 text-xs font-medium text-[var(--color-text-secondary)] opacity-0 shadow-sm transition-opacity duration-100 group-hover:opacity-100 group-focus-visible:opacity-100">
									Open GitLab profile
								</span>
							</a>
						) : (
							<div className="inline-flex shrink-0 items-center gap-1.5 text-[var(--color-text-primary)]">
								<Gitlab className="h-4 w-4 shrink-0 self-center" />
								<span className="text-sm font-semibold leading-none underline underline-offset-2">
									{profileLabel}
								</span>
							</div>
						)}
						<div className="flex-1 mx-3 min-h-8 flex items-center">
							<SearchBar
								searchQuery={searchQuery}
								onSearchChange={setSearchQuery}
								showSearch={showSearch}
								onShowSearch={setShowSearch}
							/>
						</div>
						<div className="flex items-center gap-2 shrink-0">
							<FreshnessIndicator lastUpdated={lastUpdated} />
							<NotificationBell
								items={notificationItems}
								onMarkSeen={markSeen}
								onMarkAllSeen={markAllSeen}
							/>
							<button
								type="button"
								onClick={() => setShowIconLegend(true)}
								aria-label="Open icon legend"
								title="Icon legend"
								className="flex items-center justify-center p-1 rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-opacity-50 transition-colors"
							>
								<CircleHelp className="h-4 w-4" />
							</button>
							<button
								type="button"
								onClick={refreshData}
								disabled={isRefreshing}
								aria-label={isRefreshing ? "Refreshing" : "Refresh"}
								aria-busy={isRefreshing}
								title="Refresh"
								className="flex items-center justify-center p-1 rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-opacity-50 disabled:cursor-not-allowed disabled:opacity-60 transition-colors"
							>
								<RefreshCcw
									strokeWidth={2}
									className={`${isRefreshing ? "animate-spin " : ""}transform rotate-180 w-4 h-4`}
								/>
							</button>
							<ThemeSwitcher />
							<button
								type="button"
								onClick={() => setIsFullWidth((value) => !value)}
								aria-pressed={isFullWidth}
								aria-label={
									isFullWidth
										? "Use constrained dashboard width"
										: "Use full dashboard width"
								}
								title={
									isFullWidth
										? "Use constrained dashboard width"
										: "Use full dashboard width"
								}
								className="flex items-center justify-center p-1 rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-opacity-50 transition-colors"
							>
								{isFullWidth ? (
									<Minimize2 className="h-4 w-4" />
								) : (
									<Maximize2 className="h-4 w-4" />
								)}
							</button>
							<FilterButton
								showDrafts={showDrafts}
								showBotPRs={showBotPRs}
								onToggleDrafts={setShowDrafts}
								onToggleBotPRs={setShowBotPRs}
								sortOption={sortOption}
								onSetSortOption={setSortOption}
								showPendingWithProjectRules={showPendingWithProjectRules}
								onTogglePendingWithProjectRules={setShowPendingWithProjectRules}
								showPendingWithAllApprovals={showPendingWithAllApprovals}
								onTogglePendingWithAllApprovals={setShowPendingWithAllApprovals}
								greyOutBlockingComments={greyOutBlockingComments}
								onToggleGreyOutBlockingComments={setGreyOutBlockingComments}
								enableCommentsExpansion={enableCommentsExpansion}
								onToggleCommentsExpansion={setEnableCommentsExpansion}
								viewMode={viewMode}
								onSetViewMode={setViewMode}
								showSwimlaneMergedColumn={showSwimlaneMergedColumn}
								onToggleShowSwimlaneMergedColumn={setShowSwimlaneMergedColumn}
								showSwimlaneDeletedColumn={showSwimlaneDeletedColumn}
								onToggleShowSwimlaneDeletedColumn={setShowSwimlaneDeletedColumn}
								useLocalTime={useLocalTime}
								onToggleUseLocalTime={setUseLocalTime}
								watchProjects={watchProjects}
								setWatchProjects={setWatchProjects}
								excludeProjects={excludeProjects}
								setExcludeProjects={setExcludeProjects}
								swimlaneHideCreatedDaysThreshold={
									swimlaneHideCreatedDaysThreshold
								}
								setSwimlaneHideCreatedDaysThreshold={
									setSwimlaneHideCreatedDaysThreshold
								}
								swimlaneHideUpdatedDaysThreshold={
									swimlaneHideUpdatedDaysThreshold
								}
								setSwimlaneHideUpdatedDaysThreshold={
									setSwimlaneHideUpdatedDaysThreshold
								}
							/>
							<button
								type="button"
								onClick={logout}
								className="ml-1 text-xs font-medium text-red-500 border border-red-500/40 rounded px-2.5 py-1 hover:bg-red-500/10 focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-colors"
							>
								Logout
							</button>
						</div>
					</div>
				</div>
			</div>

			<div
				className={`${dashboardWidthClassName} mb-3 mt-3 flex flex-wrap items-end justify-between gap-3`}
			>
				<div className="flex max-w-full shrink-0 flex-col gap-2">
					<TagQuickFilter
						tags={allMRTags}
						value={activeTagFilters}
						onChange={setActiveTagFilter}
						className="w-full"
						style={
							tagFilterWidth ? { width: `${tagFilterWidth}px` } : undefined
						}
					/>
					<div
						ref={tabsRef}
						className="inline-flex shrink-0 gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-1 shadow-sm"
						role="tablist"
						aria-label="Dashboard pages"
					>
						{[
							{
								id: DASHBOARD_TAB.MAIN,
								label: "Main",
								icon: GitPullRequest,
							},
							{
								id: DASHBOARD_TAB.WATCHING,
								label: "Watching",
								icon: Eye,
							},
							{
								id: DASHBOARD_TAB.REPOS,
								label: "Repos",
								icon: FolderGit2,
							},
						].map((tab) => {
							const Icon = tab.icon;
							const selected = activeTab === tab.id;
							return (
								<button
									key={tab.id}
									type="button"
									role="tab"
									aria-selected={selected}
									onClick={() => setActiveTab(tab.id)}
									className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] ${
										selected
											? "border-[var(--color-border-hover)] bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] shadow-sm hover:border-[var(--color-border-hover)]"
											: "border-transparent text-[var(--color-text-secondary)] hover:border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
									}`}
								>
									<Icon className="h-4 w-4" />
									{tab.label}
								</button>
							);
						})}
					</div>
				</div>
				<div className="ml-auto flex min-w-0 flex-wrap items-start justify-end gap-2">
					<QuickFilters
						filters={quickFilters}
						onToggle={toggleQuickFilter}
						onClear={clearQuickFilters}
						className="min-w-0 justify-end"
					/>
				</div>
			</div>

			{activeTab === DASHBOARD_TAB.WATCHING ? (
				<div className={dashboardWidthClassName}>
					<StandardView
						pendingReviewMRs={standardFilteredPendingReviewMRs}
						myOpenMRs={filteredMyOpenMRs}
						myMergedMRs={filteredMyMergedMRs}
						myClosedMRs={filteredMyClosedMRs}
						recentlyClosedMRs={filteredRecentlyClosedMRs}
						loading={loading}
						loadingClosed={loadingClosed}
						error={error}
						refetch={refetch}
						isRefreshing={isRefreshing}
						pushesWithoutMR={pushesWithoutMR}
						sortOption={sortOption}
						showPendingWithProjectRules={showPendingWithProjectRules}
						showPendingWithAllApprovals={showPendingWithAllApprovals}
						greyOutBlockingComments={greyOutBlockingComments}
						enableCommentsExpansion={enableCommentsExpansion}
						excludedMRs={standardExcludedMRs}
						watchedMRs={resolvedWatchedMRs}
						watchedMRState={watchedMRState}
						searchQuery={normalizedSearchQuery}
						excludeLikeRules={excludeLikeRules}
						onAddExcludeLikeRule={addExcludeLikeRule}
						onRemoveExcludeLikeRule={removeExcludeLikeRule}
						onClearExcludeLikeRules={clearExcludeLikeRules}
						ignoredMRIds={ignoredIds}
						onIgnoreMR={ignoreMR}
						onUnignoreMR={unignoreMR}
						onCloseMR={closeMergeRequest}
						onOpenDiff={openDiffMR}
						activeTagFilter={activeTagFilters}
						getMRTags={getMRTags}
						allMRTags={allMRTags}
						onAddMRTag={addMRTag}
						onRemoveMRTag={removeMRTag}
						watchingOnly={true}
					/>
				</div>
			) : activeTab === DASHBOARD_TAB.REPOS ? (
				<ReposPage
					fullWidth={isFullWidth}
					quickFilters={quickFilters}
					searchQuery={normalizedSearchQuery}
					showDrafts={showDrafts}
					showBotPRs={showBotPRs}
					sortOption={sortOption}
					greyOutBlockingComments={greyOutBlockingComments}
					enableCommentsExpansion={enableCommentsExpansion}
					excludeProjects={excludeProjects}
					excludeLikeRules={excludeLikeRules}
					ignoredMRIds={ignoredIds}
					onIgnoreMR={ignoreMR}
					onUnignoreMR={unignoreMR}
					onAddExcludeLikeRule={addExcludeLikeRule}
					watchedMRState={watchedMRState}
					onOpenDiff={openDiffMR}
					activeTagFilter={activeTagFilters}
					getMRTags={getMRTags}
					allMRTags={allMRTags}
					onAddMRTag={addMRTag}
					onRemoveMRTag={removeMRTag}
				/>
			) : viewMode === VIEW_MODE.SWIMLANE ? (
				<div className="px-2">
					<SwimlaneView
						pendingReviewMRs={filteredPendingReviewMRs}
						myOpenMRs={filteredMyOpenMRs}
						recentlyClosedMRs={filteredRecentlyClosedMRs}
						searchQuery={normalizedSearchQuery}
						loading={loading}
						error={error}
						showMergedColumn={showSwimlaneMergedColumn}
						showDeletedColumn={showSwimlaneDeletedColumn}
						hasIncompleteResults={hasIncompleteResults}
						hideCreatedDaysThreshold={swimlaneHideCreatedDaysThreshold}
						hideUpdatedDaysThreshold={swimlaneHideUpdatedDaysThreshold}
						enableCommentsExpansion={enableCommentsExpansion}
						onOpenDiff={openDiffMR}
						getMRTags={getMRTags}
						allMRTags={allMRTags}
						onAddMRTag={addMRTag}
						onRemoveMRTag={removeMRTag}
					/>
				</div>
			) : viewMode === VIEW_MODE.REVIEW_BOARD ? (
				<div className="px-2">
					<ReviewBoard
						pendingReviewMRs={reviewBoardPendingReviewMRs}
						myOpenMRs={reviewBoardMyOpenMRs}
						recentlyClosedMRs={reviewBoardRecentlyClosedMRs}
						searchQuery={normalizedSearchQuery}
						loading={loading}
						error={error}
						sortOption={sortOption}
						showReadyToMergeColumn={showPendingWithAllApprovals}
						showApprovedPendingColumn={showPendingWithProjectRules}
						greyOutBlockingComments={greyOutBlockingComments}
						enableCommentsExpansion={enableCommentsExpansion}
						ignoredMRIds={ignoredIds}
						onIgnoreMR={ignoreMR}
						onUnignoreMR={unignoreMR}
						onOpenDiff={openDiffMR}
						getMRTags={getMRTags}
						allMRTags={allMRTags}
						onAddMRTag={addMRTag}
						onRemoveMRTag={removeMRTag}
					/>
				</div>
			) : viewMode === VIEW_MODE.REPO ? (
				<div className="px-2">
					<RepoView
						pendingReviewMRs={filteredPendingReviewMRs}
						myOpenMRs={filteredMyOpenMRs}
						searchQuery={normalizedSearchQuery}
						loading={loading}
						error={error}
						sortOption={sortOption}
						greyOutBlockingComments={greyOutBlockingComments}
						enableCommentsExpansion={enableCommentsExpansion}
						onOpenDiff={openDiffMR}
						getMRTags={getMRTags}
						allMRTags={allMRTags}
						onAddMRTag={addMRTag}
						onRemoveMRTag={removeMRTag}
					/>
				</div>
			) : (
				<div className={dashboardWidthClassName}>
					<StandardView
						pendingReviewMRs={standardFilteredPendingReviewMRs}
						myOpenMRs={filteredMyOpenMRs}
						myMergedMRs={filteredMyMergedMRs}
						myClosedMRs={filteredMyClosedMRs}
						recentlyClosedMRs={filteredRecentlyClosedMRs}
						loading={loading}
						loadingClosed={loadingClosed}
						error={error}
						refetch={refetch}
						isRefreshing={isRefreshing}
						pushesWithoutMR={pushesWithoutMR}
						sortOption={sortOption}
						showPendingWithProjectRules={showPendingWithProjectRules}
						showPendingWithAllApprovals={showPendingWithAllApprovals}
						greyOutBlockingComments={greyOutBlockingComments}
						enableCommentsExpansion={enableCommentsExpansion}
						excludedMRs={standardExcludedMRs}
						watchedMRs={resolvedWatchedMRs}
						watchedMRState={watchedMRState}
						searchQuery={normalizedSearchQuery}
						excludeLikeRules={excludeLikeRules}
						onAddExcludeLikeRule={addExcludeLikeRule}
						onRemoveExcludeLikeRule={removeExcludeLikeRule}
						onClearExcludeLikeRules={clearExcludeLikeRules}
						ignoredMRIds={ignoredIds}
						onIgnoreMR={ignoreMR}
						onUnignoreMR={unignoreMR}
						onCloseMR={closeMergeRequest}
						onOpenDiff={openDiffMR}
						activeTagFilter={activeTagFilters}
						getMRTags={getMRTags}
						allMRTags={allMRTags}
						onAddMRTag={addMRTag}
						onRemoveMRTag={removeMRTag}
					/>
				</div>
			)}
		</div>
	);
};
